import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import logger from '@/utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext();

export function CartProvider({ children }) {
  const [cartId, setCartId] = useState(() => localStorage.getItem('morse_cart_id'));
  const [cart, setCart] = useState({ items: [], total: 0, item_count: 0 });
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const fetchCart = useCallback(async (id = null) => {
    const currentCartId = id || cartId || localStorage.getItem('morse_cart_id');
    if (!currentCartId) {
      setInitialized(true);
      return;
    }
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cart/${currentCartId}`);
      setCart(response.data);
      // Ensure cartId state is in sync
      if (!cartId && currentCartId) {
        setCartId(currentCartId);
      }
    } catch (error) {
      logger.error('Error fetching cart:', error);
      if (error.response?.status === 404) {
        localStorage.removeItem('morse_cart_id');
        setCartId(null);
        setCart({ items: [], total: 0, item_count: 0 });
      }
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [cartId]);

  useEffect(() => {
    fetchCart();
  }, []);

  const addToCart = async (productId, quantity = 1, customText = null, clasp = null, sizeCm = null) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/cart/add`, {
        cart_id: cartId,
        product_id: productId,
        quantity,
        custom_text: customText,
        clasp,
        size_cm: sizeCm,
      });
      
      const newCartId = response.data.cart_id;
      if (!cartId) {
        setCartId(newCartId);
        localStorage.setItem('morse_cart_id', newCartId);
      }
      
      await fetchCart();
      return true;
    } catch (error) {
      logger.error('Error adding to cart:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, quantity) => {
    if (!cartId) return;
    try {
      setLoading(true);
      await axios.put(`${API}/cart/${cartId}/item?product_id=${productId}&quantity=${quantity}`);
      await fetchCart();
    } catch (error) {
      logger.error('Error updating cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (productId) => {
    if (!cartId) return;
    try {
      setLoading(true);
      await axios.delete(`${API}/cart/${cartId}/item/${productId}`);
      await fetchCart();
    } catch (error) {
      logger.error('Error removing from cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearCart = () => {
    localStorage.removeItem('morse_cart_id');
    setCartId(null);
    setCart({ items: [], total: 0, item_count: 0 });
  };

  return (
    <CartContext.Provider value={{
      cart,
      cartId,
      loading,
      initialized,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      fetchCart
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
